<template>
  <div>
    <div class="account-statement-view">
      <div class="actions mb-3 d-flex justify-content-between">
        <b-button
          size="lg"
          variant="outline-secondary"
          class="close-btn"
          @click="$emit('print-closed')"
        >
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          {{ $t("common.close") || "Close" }}
        </b-button>
        <b-button
          size="lg"
          variant="success"
          class="print-btn"
          :disabled="isGenerating"
          @click="generatePdf"
        >
          <feather-icon
            v-if="!isGenerating"
            icon="PrinterIcon"
            size="18"
            class="mr-50"
          />
          <b-spinner v-if="isGenerating" small class="mr-50" />
          <span v-if="isGenerating">{{
            $t("common.loading") || "Loading..."
          }}</span>
          <span v-else>{{ $t("common.print") || "Print" }}</span>
        </b-button>
      </div>

      <div
        id="printableContent"
        ref="printContent"
        class="statement-content"
        dir="rtl"
      >
        <div class="header">
          <div class="brand">
            <div class="brand-title">
              {{ statementTitle }}
            </div>
            <div class="brand-sub">
              {{ $t("reception.patient-name") }}: {{ patientName }}
            </div>
          </div>
          <div class="date-info">
            <div>{{ $t("common.date") }}: {{ printedAt }}</div>
          </div>
        </div>

        <div class="meta-grid">
          <div class="meta-item">
            <span class="label">{{ $t("common.total-items") }}:</span>
            <span class="value">{{ data.length }}</span>
          </div>
          <!-- <div class="meta-item">
            <span class="label">{{ $t("common.total-amount") }}:</span>
            <span class="value">{{ formatAmount(totalAmount) }}</span>
          </div> -->
        </div>

        <!-- Paid items -->
        <h4 class="section-title">الفواتير المدفوعة</h4>
        <table class="statement-table">
          <thead>
            <tr>
              <th>التاريخ</th>
              <th>رقم الوصل</th>
              <th>الخدمة الطبية</th>
              <th>حالة الدفع</th>
              <th>المبلغ</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in settledItems" :key="'settled-' + index">
              <td>{{ getDate(item) }}</td>
              <td>{{ getInvoiceNumber(item) }}</td>
              <td>{{ getServiceName(item) }}</td>
              <td>{{ getPaymentStatus(item) }}</td>
              <td class="amount-cell">
                {{ formatAmount(getAmount(item)) }}
              </td>
            </tr>
          </tbody>
          <tfoot v-if="settledItems.length">
            <tr>
              <td colspan="4" class="text-right font-weight-bold">المجموع</td>
              <td class="font-weight-bold amount-cell">
                {{ formatAmount(settledTotal) }}
              </td>
            </tr>
          </tfoot>
        </table>

        <!-- Unpaid / pending items -->
        <h4 v-if="pendingItems.length" class="section-title mt-2">
          الفواتير غير المدفوعة
        </h4>
        <table v-if="pendingItems.length" class="statement-table">
          <thead>
            <tr>
              <th>التاريخ</th>
              <th>رقم الوصل</th>
              <th>الخدمة الطبية</th>
              <th>حالة الدفع</th>
              <th>المبلغ</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in pendingItems" :key="'pending-' + index">
              <td>{{ getDate(item) }}</td>
              <td>{{ getInvoiceNumber(item) }}</td>
              <td>{{ getServiceName(item) }}</td>
              <td>{{ getPaymentStatus(item) }}</td>
              <td class="amount-cell">
                {{ formatAmount(getAmount(item)) }}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="4" class="text-right font-weight-bold">
                مجموع الغير مدفوع
              </td>
              <td class="font-weight-bold amount-cell">
                {{ formatAmount(pendingTotal) }}
              </td>
            </tr>
          </tfoot>
        </table>

        <h4 v-if="refundItems.length" class="section-title mt-2">
          فواتير الاسترجاع
        </h4>
        <table v-if="refundItems.length" class="statement-table">
          <thead>
            <tr>
              <th>التاريخ</th>
              <th>رقم الوصل</th>
              <th>الخدمة الطبية</th>
              <th>حالة الدفع</th>
              <th>المبلغ</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in refundItems" :key="'refund-' + index">
              <td>{{ getDate(item) }}</td>
              <td>{{ getInvoiceNumber(item) }}</td>
              <td>{{ getServiceName(item) }}</td>
              <td>{{ getPaymentStatus(item) }}</td>
              <td class="amount-cell">
                {{ formatAmount(getAmount(item)) }}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="4" class="text-right font-weight-bold">
                مجموع الاسترجاع
              </td>
              <td class="font-weight-bold amount-cell">
                {{ formatAmount(refundTotal) }}
              </td>
            </tr>
          </tfoot>
        </table>

        <div v-if="refundItems.length" class="net-total-summary">
          <div class="net-total-row">
            <span class="label">مجموع الفواتير:</span>
            <span class="value">{{ formatAmount(invoicesSubtotal) }}</span>
          </div>
          <div class="net-total-row">
            <span class="label">مجموع الاسترجاع:</span>
            <span class="value">- {{ formatAmount(refundTotal) }}</span>
          </div>
          <div class="net-total-row net-total-final">
            <span class="label">المجموع الصافي:</span>
            <span class="value">{{ formatAmount(netTotal) }}</span>
          </div>
        </div>

        <div class="footer-note">
          <div class="signature">
            <div class="sig-line" />
            <div class="sig-label">
              {{ $t("common.signature") }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PatientAccountStatement",
  props: {
    data: {
      type: Array,
      required: true,
      default: () => [],
    },
    patientName: {
      type: String,
      default: "",
    },
    totalAmount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      isGenerating: false,
      printedAt: new Date().toLocaleString(),
    };
  },
  computed: {
    statementTitle() {
      return "كشف حساب المريض";
    },
    regularItems() {
      return (this.data || []).filter(item => !this.isRefund(item));
    },
    refundItems() {
      return (this.data || []).filter(
        item => this.isRefund(item) && !this.isPending(item) && !this.isRejected(item),
      );
    },
    settledItems() {
      return this.regularItems.filter(
        item => !this.isPending(item) && !this.isRejected(item),
      );
    },
    pendingItems() {
      return this.regularItems.filter(item => this.isPending(item));
    },
    settledTotal() {
      return this.settledItems.reduce(
        (sum, item) => sum + this.getAmount(item),
        0
      );
    },
    pendingTotal() {
      return this.pendingItems.reduce(
        (sum, item) => sum + this.getAmount(item),
        0
      );
    },
    refundTotal() {
      return this.refundItems.reduce(
        (sum, item) => sum + this.getAmount(item),
        0
      );
    },
    invoicesSubtotal() {
      return this.settledTotal + this.pendingTotal;
    },
    netTotal() {
      return this.invoicesSubtotal - this.refundTotal;
    },
  },
  methods: {
    formatAmount(amount) {
      if (amount == null || amount === "") return "-";
      try {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "IQD",
          maximumFractionDigits: 0,
        }).format(Number(amount));
      } catch (e) {
        return amount;
      }
    },
    getServiceName(item) {
      // Try to get service name from various sources (prefer explicit names)
      if (item.service_name) return item.service_name;
      if (item.surgery_name) return item.surgery_name;
      if (item.service && (item.service.title || item.service.name)) {
        return item.service.title || item.service.name;
      }
      if (item.surgery && item.surgery.title) {
        return item.surgery.title;
      }
      // Get type name from translation as a last fallback
      const typeMap = {
        invoice_out_clinic: "cashiers.invoice-type-out-clinic",
        invoice_nicu: "cashiers.invoice-type-nicu",
        invoice_patient_card_test: "cashiers.invoice-type-patient-card-test",
        refund: "cashiers.invoice-type-refund",
        invoice_insurance_surgery: "cashiers.invoice-type-insurance-surgery",
        invoice_emergency: "cashiers.invoice-type-emergency",
      };
      const translationKey = typeMap[item.type];
      if (translationKey) {
        return this.$t(translationKey);
      }
      return item.type || "-";
    },
    getInvoiceNumber(item) {
      return item.invoice_no || "-";
    },
    getAmount(item) {
      return parseFloat(item.amount || 0);
    },
    getDate(item) {
      return item.date || item.created_at || "-";
    },
    isPending(item) {
      const status = (item.status || "").toString().toLowerCase();
      return status === "pending";
    },
    isRejected(item) {
      const status = (item.status || "").toString().toLowerCase();
      return status === "rejected";
    },
    isRefund(item) {
      return (item.type || "").toString().toLowerCase() === "refund";
    },
    getPaymentStatus(item) {
      const status = (item.status || "").toString().toLowerCase();
      switch (status) {
        case "pending":
          return "غير مدفوع";
        case "refunded":
        case "refund":
          return "مسترجع";
        case "canceled":
        case "cancelled":
          return "ملغى";
        default:
          // Any non-pending, non-refund, non-cancel status is treated as paid
          return "مدفوع";
      }
    },
    generatePdf() {
      this.isGenerating = true;
      const content = this.$refs.printContent;
      if (!content) {
        this.isGenerating = false;
        return;
      }

      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        alert(
          this.$t("common.popup-blocked") || "Please allow popups for this site"
        );
        this.isGenerating = false;
        return;
      }

      const htmlContent = `
<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${this.statementTitle}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
      padding: 12px;
      color: #111827;
      line-height: 1.4;
      font-size: 11px;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
      border-bottom: 1px solid #e5e7eb;
      padding-bottom: 8px;
    }
    .brand-title {
      font-size: 16px;
      font-weight: 900;
      color: #111827;
      margin-bottom: 4px;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    .brand-sub {
      font-size: 13px;
      font-weight: 600;
      color: #1f2937;
      margin-top: 4px;
    }
    .brand-info {
      font-size: 11px;
      color: #6b7280;
      margin-top: 5px;
    }
    .meta-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 8px;
      margin-bottom: 10px;
      padding: 8px;
      background: #f9fafb;
      border-radius: 8px;
    }
    .meta-item {
      display: flex;
      flex-direction: column;
    }
    .meta-item .label {
      font-size: 12px;
      color: #6b7280;
      font-weight: 600;
      text-transform: uppercase;
    }
    .meta-item .value {
      font-size: 13px;
      color: #111827;
      font-weight: 700;
      margin-top: 4px;
    }
    .statement-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 16px;
    }
    .statement-table th,
    .statement-table td {
      padding: 6px 8px;
      text-align: right;
      border: 1px solid #e5e7eb;
    }
    .statement-table th {
      background: #f3f4f6;
      font-weight: 700;
      color: #111827;
      font-size: 11px;
    }
    .statement-table tfoot td {
      background: #f9fafb;
      font-weight: 700;
    }
    .net-total-summary {
      margin: 12px 0 16px;
      padding: 10px;
      background: #f9fafb;
      border-radius: 8px;
      border: 1px solid #e5e7eb;
    }
    .net-total-row {
      display: flex;
      justify-content: space-between;
      padding: 4px 0;
      font-size: 12px;
    }
    .net-total-row .label {
      font-weight: 600;
      color: #4b5563;
    }
    .net-total-row .value {
      font-weight: 700;
      color: #111827;
    }
    .net-total-final {
      margin-top: 6px;
      padding-top: 8px;
      border-top: 1px solid #d1d5db;
      font-size: 13px;
    }
    .net-total-final .label,
    .net-total-final .value {
      font-weight: 800;
      color: #111827;
    }
    .footer-note {
      margin-top: 20px;
      display: flex;
      justify-content: flex-end;
    }
    .signature {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .sig-line {
      width: 140px;
      height: 1px;
      background: #9ca3af;
      margin-bottom: 4px;
    }
    .sig-label {
      font-size: 12px;
      color: #4b5563;
      font-weight: 600;
    }
    @media print {
      body {
        padding: 10mm;
      }
      .statement-table {
        page-break-inside: auto;
      }
      .statement-table tr {
        page-break-inside: avoid;
      }
      .statement-table thead {
        display: table-header-group;
      }
    }
  </style>
</head>
<body>
  ${content.innerHTML}
  <script>
    window.onload = function() {
      window.print();
    };
  <\/script>
</body>
</html>
      `;

      printWindow.document.write(htmlContent);
      printWindow.document.close();
      this.$emit("print-started");

      // Listen for print window close
      const checkClosed = setInterval(() => {
        if (printWindow.closed) {
          clearInterval(checkClosed);
          this.isGenerating = false;
          // Emit event to parent to hide the component
          this.$emit("print-closed");
        }
      }, 500);

      // Also handle after print dialog closes (after printing or canceling)
      printWindow.onbeforeunload = () => {
        clearInterval(checkClosed);
        this.isGenerating = false;
        this.$emit("print-closed");
      };

      // Listen for print dialog close (after print or cancel)
      // This will fire when the print dialog is dismissed
      const handlePrintDialogClose = () => {
        // Wait a bit to ensure the print window is still open
        setTimeout(() => {
          if (printWindow.closed) {
            clearInterval(checkClosed);
            this.isGenerating = false;
            this.$emit("print-closed");
          }
        }, 1000);
      };

      // Try to detect when print dialog closes
      // Note: This is a workaround as there's no direct event for print dialog close
      printWindow.addEventListener("focus", () => {
        // When window regains focus, print dialog likely closed
        setTimeout(() => {
          if (!printWindow.closed) {
            handlePrintDialogClose();
          }
        }, 500);
      });

      // Reset generating state after a delay
      setTimeout(() => {
        this.isGenerating = false;
      }, 1000);
    },
  },
};
</script>

<style scoped>
.account-statement-view {
  background: #fff;
  padding: 12px;
  color: #111827;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  line-height: 1.4;
  font-size: 12px;
  direction: rtl;
}

.actions {
  position: sticky;
  top: 0;
  z-index: 100;
  background: #fff;
  padding: 8px 0;
  margin: -12px -12px 12px -12px;
  padding-left: 12px;
  padding-right: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-bottom: 2px solid #10b981;
}

.close-btn {
  font-weight: 600;
  font-size: 13px;
  padding: 8px 16px;
  transition: all 0.3s ease;
}

.print-btn {
  font-weight: 700;
  font-size: 13px;
  padding: 8px 16px;
  box-shadow: 0 4px 6px rgba(16, 185, 129, 0.3);
  transition: all 0.3s ease;
}

.print-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(16, 185, 129, 0.4);
}

.print-btn:active:not(:disabled) {
  transform: translateY(0);
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 8px;
}

.brand-title {
  font-size: 18px;
  font-weight: 900;
  color: #111827;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.brand-sub {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-top: 8px;
}

.brand-info {
  font-size: 14px;
  color: #6b7280;
  margin-top: 5px;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 8px;
  margin-bottom: 12px;
  padding: 8px;
  background: #f9fafb;
  border-radius: 8px;
}

.meta-item {
  display: flex;
  flex-direction: column;
}

.meta-item .label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
}

.meta-item .value {
  font-size: 14px;
  color: #111827;
  font-weight: 700;
  margin-top: 4px;
}

.statement-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
}

.statement-table th,
.statement-table td {
  padding: 6px 8px;
  text-align: right;
  border: 1px solid #e5e7eb;
}

.statement-table th {
  background: #f3f4f6;
  font-weight: 700;
  color: #111827;
}

.amount-cell {
  text-align: right;
}

.section-title {
  margin: 10px 0;
  font-size: 16px;
  font-weight: 700;
}
.statement-table tfoot td {
  background: #f9fafb;
  font-weight: 700;
}

.net-total-summary {
  margin: 12px 0 16px;
  padding: 10px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.net-total-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 13px;
}

.net-total-row .label {
  font-weight: 600;
  color: #4b5563;
}

.net-total-row .value {
  font-weight: 700;
  color: #111827;
}

.net-total-final {
  margin-top: 6px;
  padding-top: 8px;
  border-top: 1px solid #d1d5db;
  font-size: 14px;
}

.net-total-final .label,
.net-total-final .value {
  font-weight: 800;
}

.footer-note {
  margin-top: 40px;
  display: flex;
  justify-content: flex-end;
}

.signature {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sig-line {
  width: 200px;
  height: 1px;
  background: #9ca3af;
  margin-bottom: 4px;
}

.sig-label {
  font-size: 12px;
  color: #4b5563;
  font-weight: 600;
}
</style>
